/**
 * Styles and labels shared by the Reorder chart sheet and its items
 * (kept out of the component files so fast refresh keeps working).
 */
import { rowLabel } from '@chordbaker/music-theory';
import { RowState, SectionState } from '../types';
import type { Theme } from '../contexts/ThemeContext';

export const UNTITLED = 'Untitled section';
export const EMPTY_ROW = 'Empty row';

export const createReorderStyles = (theme: Theme) => ({
  list: {
    listStyle: 'none',
    margin: 0,
    padding: theme.spacing.sm,
    overflowY: 'auto' as const,
    minHeight: 0,
  },
  item: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.sizes.hitTarget,
    borderRadius: theme.radii.md,
    backgroundColor: theme.colors.modalBackground,
    cursor: 'grab',
    userSelect: 'none' as const,
  },
  // Rows sit under their section header
  rowItem: {
    marginLeft: theme.spacing.xl,
  },
  itemStatic: {
    cursor: 'default',
  },
  itemActive: {
    zIndex: 1,
    cursor: 'grabbing',
    boxShadow: theme.shadows.popover,
  },
  handle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.sizes.hitTarget,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    color: theme.colors.textSecondary,
    borderRadius: theme.radii.md,
    // Touch drags the item instead of scrolling the list
    touchAction: 'none' as const,
  },
  // An expanded section moves by its rows, not as a whole
  handleDisabled: {
    opacity: theme.opacity.disabled,
    touchAction: 'auto' as const,
  },
  // The header's title area: shows or hides the section's rows
  toggle: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    height: theme.sizes.hitTarget,
    padding: 0,
    background: 'none',
    border: 'none',
    color: theme.colors.text,
    font: 'inherit',
    textAlign: 'left' as const,
    cursor: 'pointer',
  },
  title: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: theme.typography.sizes.lg,
    color: theme.colors.text,
  },
  rowLabel: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // Keeps the double space between chords (and no wrapping)
    whiteSpace: 'pre' as const,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.text,
  },
  rowLabelLyrics: {
    fontStyle: 'italic',
    color: theme.colors.textSecondary,
  },
  untitled: {
    fontStyle: 'italic',
    color: theme.colors.textTertiary,
  },
  pill: {
    marginLeft: theme.spacing.sm,
    display: 'flex',
  },
  // A copy made in this sheet, created on Apply
  copyTag: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.typography.sizes.sm,
    fontStyle: 'italic',
    color: theme.colors.textTertiary,
    whiteSpace: 'nowrap' as const,
  },
  rowCount: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing.sm,
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    whiteSpace: 'nowrap' as const,
  },
  chevron: {
    marginLeft: theme.spacing.xs,
    flexShrink: 0,
    color: theme.colors.textSecondary,
  },
  // Narrow so duplicate and delete read as a pair
  rowButtons: {
    display: 'flex',
    flexShrink: 0,
    marginLeft: theme.spacing.sm,
    marginRight: theme.spacing.xs,
  },
  rowButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: theme.sizes.compactButton,
    height: theme.sizes.hitTarget,
    background: 'none',
    border: 'none',
    borderRadius: theme.radii.md,
    color: theme.colors.textSecondary,
    cursor: 'pointer',
  },
  // The last section, or a section's last row, can't be deleted
  rowButtonDisabled: {
    opacity: theme.opacity.disabled,
    cursor: 'default',
  },
  note: {
    margin: 0,
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.sm,
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  srOnly: {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    overflow: 'hidden',
    clipPath: 'inset(100%)',
    whiteSpace: 'nowrap' as const,
  },
});

export type ReorderStyles = ReturnType<typeof createReorderStyles>;

export const rowCountLabel = (count: number) => `${count} ${count === 1 ? 'row' : 'rows'}`;
export const titleOf = (section: SectionState | undefined) => section?.title || UNTITLED;
export const rowText = (row: RowState) => {
  const label = rowLabel(row);
  return label.kind === 'empty' ? EMPTY_ROW : label.text;
};
